import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import {
  doc, getDoc, setDoc, collection, getDocs, query, where, limit, serverTimestamp,
} from 'firebase/firestore';

/** Permanent principal admin — always granted admin role, even if Firestore doc is missing. */
const PRIMARY_ADMIN_EMAIL = 'admin@nexia-digital.net';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  /** Load (or bootstrap) the Firestore profile doc for a logged-in user. */
  const loadProfile = async (firebaseUser) => {
    if (!firebaseUser) { setProfile(null); return null; }
    try {
      const ref = doc(db, 'users', firebaseUser.uid);
      const snap = await getDoc(ref);
      const isPrimaryAdmin = (firebaseUser.email || '').toLowerCase() === PRIMARY_ADMIN_EMAIL;

      if (snap.exists()) {
        const data = snap.data();
        // If primary admin email but role is wrong, force-restore admin role.
        if (isPrimaryAdmin && data.role !== 'admin') {
          await setDoc(ref, { ...data, role: 'admin' }, { merge: true });
          data.role = 'admin';
        }
        const p = { id: snap.id, ...data };
        setProfile(p);
        return p;
      }

      // Bootstrap admin role for:
      //   1) the hardcoded primary admin email, OR
      //   2) the first authenticated user (no admins exist yet).
      let shouldBeAdmin = isPrimaryAdmin;
      if (!shouldBeAdmin) {
        const adminQ = await getDocs(query(
          collection(db, 'users'), where('role', '==', 'admin'), limit(1)
        ));
        shouldBeAdmin = adminQ.empty;
      }

      if (shouldBeAdmin) {
        const data = {
          email: firebaseUser.email,
          name: firebaseUser.email?.split('@')[0] || 'Admin',
          role: 'admin',
          color: '#ed116c',
          createdAt: serverTimestamp(),
        };
        await setDoc(ref, data);
        const p = { id: firebaseUser.uid, ...data };
        setProfile(p);
        return p;
      }

      // Authenticated but no profile and not an admin → no access yet.
      setProfile({ id: firebaseUser.uid, email: firebaseUser.email, role: null });
      return null;
    } catch (e) {
      console.error('Failed to load profile', e);
      setProfile(null);
      return null;
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) await loadProfile(u);
      else setProfile(null);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const logout = () => signOut(auth);

  const isAdmin = profile?.role === 'admin';
  const isUser = profile?.role === 'user';

  return (
    <AuthContext.Provider value={{
      user, profile, login, logout, loading,
      isAdmin, isUser, role: profile?.role || null,
      reloadProfile: () => loadProfile(user),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
